import { useNavigate } from 'react-router-dom';
import { 
  LiveKitRoom, 
  RoomAudioRenderer,
  useTracks,
  VideoTrack,
  TrackRefContext,
  useLocalParticipant
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import { Button } from '@/components/ui/button';
import { PhoneOff, Video, VideoOff, Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import type { CallOutcome } from '@/lib/clinic/callSession';
import { useLiveKitConnection } from './useLiveKitConnection';
import { LiveKitConnectionWatcher } from './LiveKitConnectionWatcher';

interface LiveKitVideoCallProps {
  roomName: string;
  participantName: string;
  userRole: 'coach' | 'customer';
  otherParticipantName?: string;
  appointmentId?: string;
  /** `outcome.connected` is false when the other party never joined (#47). */
  onLeave?: (outcome?: CallOutcome) => void;
  forceFullscreen?: boolean;
  /** Clinic calls: log each attempt to `callAttempts` (#47). */
  attempt?: { coachId: string; customerId: string };
}

// Custom video layout component with picture-in-picture
function CustomVideoLayout({ 
  otherParticipantName, 
  language,
  dir 
}: { 
  otherParticipantName?: string; 
  language: string;
  dir: string;
}) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );
  
  const { localParticipant } = useLocalParticipant();

  // Separate local and remote tracks
  const localVideoTrack = tracks.find(
    (track) => track.participant.sid === localParticipant.sid && track.source === Track.Source.Camera
  );
  
  const remoteVideoTracks = tracks.filter(
    (track) => {
      const isRemote = track.participant.sid !== localParticipant.sid;
      const isCamera = track.source === Track.Source.Camera;
      const hasPublication = !!track.publication;
      const hasTrack = !!track.publication?.track;
      
      return isRemote && isCamera && hasPublication && hasTrack;
    }
  );

  // Log for debugging
  console.log('[LiveKit] Local participant:', localParticipant.identity, localParticipant.sid);
  console.log('[LiveKit] Remote video tracks:', remoteVideoTracks.length);

  return (
    <div className="relative w-full h-full bg-gray-900 overflow-hidden">
      {/* Main video (remote participant) */}
      {remoteVideoTracks.length > 0 ? (
        <div className="w-full h-full flex items-center justify-center overflow-hidden">
          <TrackRefContext.Provider value={remoteVideoTracks[0]}>
            <VideoTrack 
              className="min-w-full min-h-full w-auto h-auto object-cover" 
              style={{ objectPosition: 'center 30%' }}
            />
          </TrackRefContext.Provider>
          {/* Participant name overlay */}
          <div className={`absolute bottom-20 ${dir === 'rtl' ? 'right-4' : 'left-4'} bg-black/50 text-white px-3 py-1 rounded-md`}>
            {remoteVideoTracks[0].participant.name || otherParticipantName || (language === 'ar' ? 'المشارك' : 'Participant')}
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center">
          <div className="text-center text-white">
            <div className="w-24 h-24 mx-auto bg-gray-700 rounded-full flex items-center justify-center mb-6">
              <Video className="h-12 w-12 text-gray-400" />
            </div>
            <p className="text-xl font-semibold mb-2">
              {language === 'ar' ? 'في انتظار انضمام المشارك...' : 'Waiting for participant to join...'}
            </p>
            <p className="text-gray-400">
              {otherParticipantName}
            </p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="w-2 h-2 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Picture-in-Picture (local camera) - on same level as controls */}
      {localVideoTrack && (
        <div className={`absolute bottom-7 md:bottom-0 ${dir === 'rtl' ? 'left-4 md:left-2' : 'right-4 md:right-2'} w-24 h-[76px] md:w-40 md:h-28 lg:w-52 lg:h-40 bg-gray-800 rounded-lg overflow-hidden shadow-2xl border-2 border-gray-600`}>
          <TrackRefContext.Provider value={localVideoTrack}>
            <VideoTrack 
              className="min-w-full min-h-full w-auto h-auto object-cover" 
              style={{ objectPosition: 'center 30%', transform: 'scaleX(-1)' }}
            />
          </TrackRefContext.Provider>
          <div className={`absolute bottom-2 ${dir === 'rtl' ? 'right-2' : 'left-2'} bg-black/70 text-white px-2 py-1 rounded text-sm`}>
            {language === 'ar' ? 'أنت' : 'You'}
          </div>
        </div>
      )}
    </div>
  );
}

export function LiveKitVideoCall({ 
  roomName, 
  participantName, 
  userRole,
  otherParticipantName,
  appointmentId, 
  onLeave,
  forceFullscreen = false,
  attempt
}: LiveKitVideoCallProps) {
  const { language, dir } = useLanguage();
  const navigate = useNavigate();

  // Token fetch, connect timeout, retry, LiveKit errors, callAttempts (#47)
  const {
    token,
    loading,
    error,
    retry,
    finish,
    roomHandlers,
    attemptKey,
    watcherProps
  } = useLiveKitConnection({
    enabled: true,
    roomName,
    participantName,
    metadata: JSON.stringify({
      role: userRole,
      appointmentId,
      source: 'namat' // Identify as Namat for shared LiveKit project
    }),
    language,
    logPrefix: '[LiveKit]',
    attempt: attempt ? { ...attempt, role: userRole } : null
  });

  const handleEndCall = () => {
    console.log('[LiveKit] User ended call');
    const outcome = finish();
    if (outcome.connected) {
      toast.success(language === 'ar' ? 'تم إنهاء المكالمة' : 'Call ended');
    }
    if (onLeave) {
      onLeave(outcome);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className={`flex h-screen items-center justify-center bg-gray-900 ${forceFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-white mx-auto" />
          <p className="mt-4 text-white">
            {language === 'ar' ? 'جاري الاتصال...' : 'Connecting to video call...'}
          </p>
        </div>
      </div>
    );
  }

  if (error || !token) {
    return (
      <div className={`flex h-screen items-center justify-center bg-gray-900 ${forceFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        <div className="text-center p-6 bg-gray-800 rounded-lg max-w-sm mx-4">
          <p className="text-red-400 mb-4">{error || (language === 'ar' ? 'فشل الاتصال' : 'Failed to connect')}</p>
          <div className="flex items-center justify-center gap-2">
            <Button onClick={() => { void retry(); }}>
              {language === 'ar' ? 'إعادة المحاولة' : 'Retry'}
            </Button>
            <Button variant="secondary" onClick={handleEndCall}>
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen bg-gray-900 ${forceFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      <LiveKitRoom
        key={attemptKey}
        video={true}
        audio={true}
        token={token}
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
        data-lk-theme="default"
        style={{ height: '100%' }}
        onConnected={roomHandlers.onConnected}
        onDisconnected={roomHandlers.onDisconnected}
        onError={roomHandlers.onError}
      >
        <LiveKitConnectionWatcher {...watcherProps} />
        <CustomVideoLayout 
          otherParticipantName={otherParticipantName} 
          language={language}
          dir={dir}
        />
        <RoomAudioRenderer />
        
        {/* Control Buttons */}
        <div className="absolute bottom-7 md:bottom-0 left-1/2 transform -translate-x-1/2 z-50 flex gap-3 md:mb-2">
          <Button
            variant="destructive"
            size="lg"
            onClick={handleEndCall}
            className="bg-red-600 hover:bg-red-700 rounded-full w-8 h-8 md:w-10 md:h-10 p-0"
          >
            <PhoneOff className="h-3.5 w-3.5 md:h-4 md:w-4" />
          </Button>
        </div>
      </LiveKitRoom>
    </div>
  );
}
